import React, { useState, useEffect } from 'react';
import { Typography, FlexGrid, Notification } from '@telus-uds/components-web';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LabelList,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart
} from 'recharts';

import { reportsAPI, exportReportExcel } from '../utils/apiService';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import {
  formatNumber,
  formatMilliseconds,
  getDefaultDateRange
} from '../utils/formatters';
import SummaryCard from '../components/SummaryCard';
import FilterPanel from '../components/FilterPanel';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import ScopedDataNotice from '../components/ScopedDataNotice';
import { chartCardStyle, CHART_COLORS as COLORS } from '../utils/chartStyles';

const ORDER_TYPES = [
  { value: '', label: 'All' },
  { value: 'WLS', label: 'WLS' },
  { value: 'FM', label: 'Fully Managed' },
  { value: 'SOW', label: 'Statement of Work' },
  { value: 'VUL', label: 'VUL' },
  { value: 'OCDR', label: 'OCDR' },
  { value: 'CS', label: 'Cyber Security' }
];

const ORDER_STATUSES = [
  { value: '', label: 'All' },
  { value: 'INCOMPLETE', label: 'Incomplete' },
  { value: 'PENDING_VALIDATION', label: 'Pending Validation' },
  { value: 'ORDER_VALIDATED', label: 'Order Validated' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'ACTION_REQUIRED', label: 'Action Required' },
  { value: 'CONTRACT_SENT', label: 'Contract Sent' },
  { value: 'CONTRACT_SIGNED', label: 'Contract Signed' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'ORDER_CANCELED', label: 'Order Canceled' },
  { value: 'WAITING_ON_SALES', label: 'Waiting on Sales' },
  { value: 'WAITING_ON_CREDIT', label: 'Waiting on Credit' },
  { value: 'WAITING_ON_CUSTOMER_RESPONSE', label: 'Waiting on Customer Response' }
];

const PROVINCES = [
  { value: '', label: 'All' },
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon Territory' }
];

const CombinedActivityReport = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [channelOptions, setChannelOptions] = useState([{ value: '', label: 'All' }]);
  const [filterValues, setFilterValues] = useState({
    startDate: getDefaultDateRange().startDate,
    endDate: getDefaultDateRange().endDate,
    orderType: '',
    status: '',
    province: '',
    channelId: ''
  });
  // Snapshot of whichever filters produced the data currently on screen — export must use
  // this, not the live filterValues, or editing a filter without clicking Apply would export
  // a file that doesn't match what's displayed.
  const [appliedFilters, setAppliedFilters] = useState(null);

  const fetchData = async (customFilters = null) => {
    setLoading(true);
    setError(null);
    try {
      const filters = customFilters || filterValues;
      setAppliedFilters(filters);
      const result = await reportsAPI.getCombinedActivity(filters);
      setData(result.data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to fetch combined activity data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    reportsAPI.getChannels()
      .then(result => {
        const channels = (result && result.channels) || [];
        setChannelOptions([
          { value: '', label: 'All' },
          ...channels.map(c => ({ value: c.id, label: c.description || c.name }))
        ]);
      })
      .catch(() => {
        // Non-critical: leave the Channel filter as "All" only if the list fails to load.
      });
  }, []);

  const handleFilterChange = (name, value) => {
    setFilterValues(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    fetchData();
  };

  const handleResetFilters = () => {
    const defaultFilters = {
      startDate: getDefaultDateRange().startDate,
      endDate: getDefaultDateRange().endDate,
      orderType: '',
      status: '',
      province: '',
      channelId: ''
    };
    setFilterValues(defaultFilters);
    // Fetch data immediately with default filters
    fetchData(defaultFilters);
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const exportFilters = appliedFilters || filterValues;
      const fileName = `combined-activity-report-${exportFilters.startDate}-to-${exportFilters.endDate}.xlsx`;
      await exportReportExcel('/reports/combined-activity/export/excel', exportFilters, fileName);
    } catch (err) {
      alert('Failed to export Excel file. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const filters = [
    {
      name: 'startDate',
      label: 'Start Date',
      type: 'date',
      value: filterValues.startDate,
      placeholder: 'YYYY-MM-DD'
    },
    {
      name: 'endDate',
      label: 'End Date',
      type: 'date',
      value: filterValues.endDate,
      placeholder: 'YYYY-MM-DD'
    },
    {
      name: 'orderType',
      label: 'Order Type',
      type: 'select',
      value: filterValues.orderType,
      options: ORDER_TYPES
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      value: filterValues.status,
      options: ORDER_STATUSES
    },
    {
      name: 'province',
      label: 'Province',
      type: 'select',
      value: filterValues.province,
      options: PROVINCES
    },
    {
      name: 'channelId',
      label: 'Channel',
      type: 'select',
      value: filterValues.channelId,
      options: channelOptions
    }
  ];

  if (loading) {
    return (
      <div>
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} onApply={handleApplyFilters} onReset={handleResetFilters} />
        <LoadingSpinner text="Loading Combined Activity Dashboard..." />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} onApply={handleApplyFilters} onReset={handleResetFilters} />
        <Notification variant={{ validation: 'error' }}>
          <div>
            <strong>Error:</strong> {error}
          </div>
        </Notification>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} onApply={handleApplyFilters} onReset={handleResetFilters} />
        <EmptyState title="No Data Available" />
      </div>
    );
  }

  const { orders, pdfs, emails } = data;

  // Prepare orders data
  const ordersTypeData = orders && orders.by_type && orders.by_type.map(item => ({
    name: item.type_of_order || 'Unknown',
    value: parseInt(item.count, 10)
  })) || [];

  const ordersStatusData = orders && orders.by_status && orders.by_status.map(item => ({
    name: (item.status || '').replace(/_/g, ' '),
    count: parseInt(item.count, 10)
  })) || [];

  // Prepare PDFs data
  const pdfTypeData = pdfs && pdfs.by_type && pdfs.by_type.map(item => ({
    name: item.pdf_type || 'Unknown',
    value: parseInt(item.count, 10)
  })) || [];

  const pdfSuccessItem = pdfs && pdfs.status && pdfs.status.find(s => s.status === 'SUCCESS');
  const pdfFailedItem = pdfs && pdfs.status && pdfs.status.find(s => s.status === 'FAILED');
  const pdfSuccessCount = parseInt((pdfSuccessItem && pdfSuccessItem.count) || 0, 10);
  const pdfFailedCount = parseInt((pdfFailedItem && pdfFailedItem.count) || 0, 10);
  const totalPdfs = (pdfs && pdfs.summary && pdfs.summary.total_pdfs_generated) || 0;
  const pdfSuccessRate = totalPdfs > 0 ? ((pdfSuccessCount / totalPdfs) * 100).toFixed(2) : 0;

  // Prepare emails data
  const emailTypeData = emails && emails.by_type && emails.by_type.slice(0, 5).map(item => ({
    name: (item.type || '').replace(/_/g, ' '),
    count: parseInt(item.count, 10)
  })) || [];

  const emailSuccessItem = emails && emails.status && emails.status.find(s => s.status === 'SUCCESS');
  const emailSuccessCount = parseInt((emailSuccessItem && emailSuccessItem.count) || 0, 10);
  const totalEmails = (emails && emails.summary && emails.summary.total_emails_sent) || 0;
  const emailSuccessRate = totalEmails > 0 ? ((emailSuccessCount / totalEmails) * 100).toFixed(2) : 0;

  // Prepare combined hourly data. Bug: this used to iterate orders.hourly_distribution only,
  // so hours with PDF/email activity but zero orders were dropped from the chart entirely —
  // union the hours from all three distributions instead.
  const ordersHourly = (orders && orders.hourly_distribution) || [];
  const pdfsHourly = (pdfs && pdfs.hourly_distribution) || [];
  const emailsHourly = (emails && emails.hourly_distribution) || [];
  const allHours = Array.from(new Set([
    ...ordersHourly.map(item => item.hour),
    ...pdfsHourly.map(item => item.hour),
    ...emailsHourly.map(item => item.hour)
  ])).sort((a, b) => a - b);

  const combinedHourlyData = allHours.map(hour => {
    const orderHour = ordersHourly.find(o => o.hour === hour);
    const pdfHour = pdfsHourly.find(p => p.hour === hour);
    const emailHour = emailsHourly.find(e => e.hour === hour);

    return {
      hour: `${hour}:00`,
      orders: orderHour ? parseInt(orderHour.count, 10) : 0,
      pdfs: pdfHour ? parseInt(pdfHour.count, 10) : 0,
      emails: emailHour ? parseInt(emailHour.count, 10) : 0
    };
  });

  return (
    <div>
      {error && (
        <Notification variant={{ validation: 'error' }}>
          <div>
            <strong>Error:</strong> {error}
          </div>
        </Notification>
      )}
      <FilterPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        onExport={handleExportExcel}
        isExporting={isExporting}
      />
      <ScopedDataNotice />

      {/* Overview Summary Cards */}
      <div style={{ marginBottom: '2rem' }}>
        <Typography heading="h2" variant={{ size: 'h2' }} block style={{ color: '#4b286d', marginBottom: '1.5rem' }}>Overview</Typography>
        <FlexGrid>
          <FlexGrid.Row>
            <FlexGrid.Col xs={12} md={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
              <SummaryCard
                title="Total Orders"
                value={formatNumber(orders && orders.total_orders)}
                icon="📦"
                color="#0066CC"
              />
            </FlexGrid.Col>
            <FlexGrid.Col xs={12} md={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
              <SummaryCard
                title="Total PDFs Generated"
                value={formatNumber(totalPdfs)}
                icon="📄"
                color="#4b286d"
              />
            </FlexGrid.Col>
            <FlexGrid.Col xs={12} md={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
              <SummaryCard
                title="Total Emails Sent"
                value={formatNumber(totalEmails)}
                icon="📧"
                color="#00A600"
              />
            </FlexGrid.Col>
          </FlexGrid.Row>
        </FlexGrid>
      </div>

      {/* Combined Hourly Chart */}
      <div
        style={{
          background: '#fff',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid #e4e4e4',
          marginBottom: '3rem'
        }}
      >
        <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
          Combined Activity - Hourly Distribution
        </Typography>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={combinedHourlyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="hour" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="orders" stroke="#0066CC" strokeWidth={2} name="Orders" />
            <Line type="monotone" dataKey="pdfs" stroke="#4b286d" strokeWidth={2} name="PDFs" />
            <Line type="monotone" dataKey="emails" stroke="#00A600" strokeWidth={2} name="Emails" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Orders Section */}
      <div style={{ marginBottom: '3rem' }}>
        <Typography heading="h2" variant={{ size: 'h2' }} block style={{ color: '#4b286d', marginBottom: '1.5rem' }}>Orders</Typography>
        <FlexGrid>
          <FlexGrid.Row>
            <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
              <div
                style={chartCardStyle}
              >
                <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                  Orders by Type
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={ordersTypeData}
                      cx="38%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                    >
                    {ordersTypeData.map((entry) => (
                      <Cell
                        key={`cell-${entry.name}`}
                        fill={COLORS[ordersTypeData.indexOf(entry) % COLORS.length]}
                      />
                    ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatNumber(value)} />
                    <Legend
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                      formatter={(value, entry) => `${value}: ${formatNumber(entry.payload.value)}`}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </FlexGrid.Col>

            <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
              <div
                style={chartCardStyle}
              >
                <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                  Orders by Status
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={ordersStatusData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={110} interval={0} tick={{ fontSize: 10 }} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#0066CC" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </FlexGrid.Col>
          </FlexGrid.Row>
        </FlexGrid>
      </div>

      {/* PDFs Section */}
      <div style={{ marginBottom: '3rem' }}>
        <Typography heading="h2" variant={{ size: 'h2' }} block style={{ color: '#4b286d', marginBottom: '1.5rem' }}>PDFs</Typography>
        <FlexGrid>
          <FlexGrid.Row>
            <FlexGrid.Col xs={12} md={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
              <SummaryCard
                title="Total PDFs"
                value={formatNumber(totalPdfs)}
                icon="📄"
              />
            </FlexGrid.Col>
            <FlexGrid.Col xs={12} md={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
              <SummaryCard
                title="Success Rate"
                value={`${pdfSuccessRate}%`}
                color={pdfSuccessRate >= 95 ? '#00A600' : '#FF9900'}
                badge={{
                  text: `${pdfFailedCount} failed`,
                  background: pdfFailedCount > 0 ? '#ffebee' : '#e8f5e9',
                  color: pdfFailedCount > 0 ? '#CC0000' : '#00A600'
                }}
              />
            </FlexGrid.Col>
            <FlexGrid.Col xs={12} md={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
              <SummaryCard
                title="Avg Generation Time"
                value={formatMilliseconds(pdfs && pdfs.performance && pdfs.performance.avg_generation_time_ms)}
                subText={`Max: ${formatMilliseconds(pdfs && pdfs.performance && pdfs.performance.max_generation_time_ms)}`}
              />
            </FlexGrid.Col>
          </FlexGrid.Row>

          <FlexGrid.Row>
            <FlexGrid.Col xs={12} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
              <div
                style={chartCardStyle}
              >
                <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                  PDF Types
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={pdfTypeData}
                      cx="38%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pdfTypeData.map((entry) => (
                        <Cell
                          key={`cell-pdf-${entry.name}`}
                          fill={COLORS[pdfTypeData.indexOf(entry) % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatNumber(value)} />
                    <Legend
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                      formatter={(value, entry) => `${value}: ${formatNumber(entry.payload.value)}`}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </FlexGrid.Col>
          </FlexGrid.Row>
        </FlexGrid>
      </div>

      {/* Emails Section */}
      <div style={{ marginBottom: '3rem' }}>
        <Typography heading="h2" variant={{ size: 'h2' }} block style={{ color: '#4b286d', marginBottom: '1.5rem' }}>Emails</Typography>
        <FlexGrid>
          <FlexGrid.Row>
            <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
              <SummaryCard
                title="Total Emails"
                value={formatNumber(totalEmails)}
                icon="📧"
              />
            </FlexGrid.Col>
            <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
              <SummaryCard
                title="Success Rate"
                value={`${emailSuccessRate}%`}
                color={emailSuccessRate >= 95 ? '#00A600' : '#FF9900'}
                badge={{
                  text: `${formatNumber(emailSuccessCount)} successful`,
                  background: '#e8f5e9',
                  color: '#00A600'
                }}
              />
            </FlexGrid.Col>
          </FlexGrid.Row>

          <FlexGrid.Row>
            <FlexGrid.Col xs={12} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
              <div
                style={chartCardStyle}
              >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Top Email Types
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={emailTypeData} layout="vertical" margin={{ right: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={180} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => formatNumber(value)} />
                  <Legend />
                  <Bar dataKey="count" fill="#00A600" name="Email Count" radius={[0, 4, 4, 0]}>
                    <LabelList dataKey="count" position="right" formatter={formatNumber} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              </div>
            </FlexGrid.Col>
          </FlexGrid.Row>
        </FlexGrid>
      </div>
    </div>
  );
};

export default CombinedActivityReport;
